import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { army_list_unit, army_list_unit_dto } from '@Api';
import {
  ArmyListUnit,
  ArmyListUnitVersioning,
  EntityVersioning,
  Nullable,
} from '@Models';
import { ArmyListUnitUnitOptionMapper } from './army-list-unit-unit-option.mapper';
import { UnitCaracMapper } from '../ninthAge/UnitCaracMapper';
import { ArmyListUnitTroopMapper } from './army-list-unit-troop.mapper';
import { ArmyListUnitModelRuleTroopMapper } from './army-list-unit-model-rule-troop.mapper';
import { IMapper } from '../IMapper';
import { OrganisationMapper } from '../ninthAge/OrganisationMapper';
import { UnitMapper } from '@Mapper/ninthAge/UnitMapper';

@injectable()
export class ArmyListUnitMapper
  implements IMapper<string, army_list_unit, army_list_unit, ArmyListUnit>
{
  @inject(TYPES.ArmyListUnitUnitOptionMapper)
  private _armyListUnitUnitOptionMapper!: ArmyListUnitUnitOptionMapper;
  @inject(TYPES.UnitCaracMapper) private _unitCaracMapper!: UnitCaracMapper;
  @inject(TYPES.UnitMapper) private _unitMapper!: UnitMapper;
  @inject(TYPES.Mapper.ArmyListUnitTroopMapper)
  private _armyListUnitTroopMapper!: ArmyListUnitTroopMapper;
  @inject(TYPES.Mapper.ArmyListUnitModelRuleTroopMapper)
  private _modelRuleTroopMapper!: ArmyListUnitModelRuleTroopMapper;
  @inject(TYPES.OrganisationMapper)
  private _organisationMapper!: OrganisationMapper;

  public map(item: army_list_unit): ArmyListUnit {
    return {
      id: item.id,
      updatedAt: item.updated_at,
      syncId: null,
      errorSync: false,
      organisation_id: item.organisation_id,
      organisations:
        item.organisations?.map((item) => this._organisationMapper.map(item)) ??
        [],
      points: item.points,
      size: item.size,
      carac: this._unitCaracMapper.map(item.carac),
      position: item.position,
      is_enabled: item.is_enabled,
      unit_id: item.unit_id,
      unitName: item.unit_name,
      mountId: item.mount_id,
      mount: item.mount != null ? this._unitMapper.map(item.mount) : null,
      isDeleted: false,
      name: item.name,
      notes: item.notes,
      magic: item.magic,
      army_list_unit_unit_options: item.army_list_unit_unit_options.map(
        (item) => this._armyListUnitUnitOptionMapper.map(item),
      ),
      troops: item.troops.map((item) =>
        this._armyListUnitTroopMapper.map(item),
      ),
      modelRuleUnitTroops: item.army_list_unit_model_rule_troops.map((item) =>
        this._modelRuleTroopMapper.map(item),
      ),
    } satisfies ArmyListUnit as ArmyListUnit;
  }

  public mapDto(item: ArmyListUnit): army_list_unit_dto {
    return {
      id: item.id != '' ? item.id : null,
      size: item.size,
      position: item.position,
      is_enabled: item.is_enabled,
      _destroy: item.isDeleted,
      value_points: item.points,
      unit_id: item.unit_id,
      mount_id: item.mountId,
      unit_base_id: item.carac.unitBase.id,
      unit_height_id: item.carac.unitHeight.id,
      unit_type_id: item.carac?.unitType?.id ?? null,
      organisation_id: item.organisation_id,
      name: item.name,
      notes: item.notes,
      magic: item.magic,

      carac_ground_adv: item.carac.ground_adv ?? null,
      carac_ground_mar: item.carac.ground_mar ?? null,
      carac_fly_adv: item.carac.fly_adv ?? null,
      carac_fly_mar: item.carac.fly_mar ?? null,
      carac_dis: item.carac.dis ?? null,
      carac_evoked: item.carac.evoked,
      carac_hp: item.carac.hp,
      carac_def: item.carac.def,
      carac_res: item.carac.res,
      carac_arm: item.carac.arm,
      carac_aeg: item.carac.aeg,

      army_list_unit_unit_options_attributes:
        item.army_list_unit_unit_options.map((o) =>
          this._armyListUnitUnitOptionMapper.mapDto(o),
        ),
      army_list_unit_troops_attributes: item.troops.map((t) =>
        this._armyListUnitTroopMapper.mapDto(t),
      ),
      army_list_unit_model_rule_troops_attributes: item.modelRuleUnitTroops.map(
        (m) => this._modelRuleTroopMapper.mapDto(m),
      ),
    } satisfies army_list_unit_dto as army_list_unit_dto;
  }

  mapFull(data: army_list_unit): ArmyListUnit {
    return this.map(data);
  }

  mapVersioning(item: army_list_unit): Nullable<EntityVersioning<string>> {
    return {
      id: item.id,
      name: item.name,
      updatedAt: item.updated_at,
    } satisfies ArmyListUnitVersioning as ArmyListUnitVersioning;
  }
}
